import React, { useState, useRef, useEffect } from 'react'
import { FilledButton, OutlinedButton } from '@sainsburys-tech/luna-react'

import { ContractorManagementType } from '../../types/Entities/ContractorManagementType'
import contractorContactsApi from '../../api/contractorContactsApi'
import contractorManagementApi from '../../api/contractorManagementApi'
import { useApiClient } from '../../context/AppContext'
import FormAlerts from '../Common/ContractorForm/FormAlerts'
import { Switch, TextInputField } from '@sainsburys-tech/luna-react'
import ConfirmationModal from '../Common/ContractorForm/ConfirmationModal'
import { Card,  FormGroup} from '@sainsburys-tech/luna-react';


const ContractorManagement = () => {
  const apiClient = useApiClient()
  const [errorAlert, setErrorAlert] = useState<false | string>(false)
  const [successAlert, setSuccessAlert] = useState<false | string>(false)

  const [companyRows, setCompanyRows] = useState<ContractorManagementType[]>([
    { companyId: '1', companyName: '', isEnabled: false, isNew: 1 }
  ])
  const [modalState, setModalState] = useState<"save" | "discard" | false>( false );
  const scrollToTop = () => document.querySelector('div.ln-o-container.content')?.scrollTo(0, 0)
  const scrollToGivenIndex = (div: string) => {
    setTimeout(() => {
      const lastIndexElement = document.querySelector(div);
      if (lastIndexElement) {
        lastIndexElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 100);
  }

  useEffect(() => {
    loadCompany()
  }, [])

  const loadCompany = async (): Promise<ContractorManagementType[]> => {
    return contractorManagementApi
      .getCompanyContractordetails(apiClient)
      .then((response) => {
        const mappedResponse = response.map((item) => ({
          ...item, isNew: 0
        })).sort((a, b) => a.companyName.localeCompare(b.companyName))
        setCompanyRows(mappedResponse)
        return mappedResponse
      })
      .catch(error => {
        console.log(error)
        setErrorAlert('Something went wrong!')
        scrollToTop()
        const event = new Event('click', { bubbles: true })
        document.querySelector("button.ln-c-button.ln-c-input-group__action, button[aria-label='close menu']")?.dispatchEvent(event)
        return Promise.resolve([])
      })
  }

  const handleAddCompany = () => {
    setCompanyRows([...companyRows, { companyId: new Date().getTime().toString(), companyName: '', isEnabled: false, isNew: 1}])
    scrollToGivenIndex('.scroll-last-index');
    clearAlerts();
  }

  const handleCompanyNameChange = (companyId: string, value: string) => {
    setCompanyRows(companyRows.map(row => 
      row.companyId === companyId ? { ...row, companyName: value, isNew: row.isNew === 1 ? 1 : 2 } : row
    ))
    clearAlerts();
  }

  const handleAutomationChange = (companyId: string, checked: boolean) => {
    setCompanyRows(companyRows.map(row => 
      row.companyId === companyId ? { ...row, isEnabled: checked, isNew: row.isNew === 1 ? 1 : 2 } : row
    ))
    clearAlerts();
  }

  const resetPage = () => {
    clearAlerts()
    loadCompany()
  }

  const clearAlerts = () => {
    setErrorAlert(false)
    setSuccessAlert(false)
  }

  const handleConfirmDiscard = () => {
    setModalState(false);
    resetPage();
    scrollToGivenIndex('.pkr');
  };


  const handleSaveForm = () => {
    const hasEmptyNames = companyRows.some(row => !row.companyName?.trim() && row.isNew === 2);

    const modifiedRows = companyRows.some(row => row.isNew !== 0 && row.companyName?.trim())

    if (hasEmptyNames) {
      setErrorAlert('Company name cannot be empty. Please provide a valid company name.');
      setSuccessAlert(false);
      scrollToTop();
      return;
    }
    if(!modifiedRows){
      setSuccessAlert('No changes to save.')
      setErrorAlert(false) 
      scrollToTop();
      return;
    }

    const companyNames = companyRows
      .map(row => row.companyName?.trim().toLowerCase())
      .filter(name => name); 

    const duplicates = companyNames.filter((name, index) => 
      companyNames.indexOf(name) !== index
    );

    if (duplicates.length > 0) {
      setErrorAlert('Duplicate company names are not allowed. Please ensure all company names are unique.');
      setSuccessAlert(false);
      scrollToTop();
      return;
    }
    setModalState('save')
  }

  const handleConfirmSave = () => {
    setModalState(false);
    const modifiedRows = companyRows
      .filter(row => row.isNew !== 0 && row.companyName?.trim())
      .map(row => ({ ...row, companyId: row.isNew === 1 ? '' : row.companyId }))
      .map(({ isNew, ...rest }) => rest)
    contractorManagementApi
      .postCompanyContractorDetails(apiClient, modifiedRows)
      .then(response => {
        if (response.ok) {
          setSuccessAlert('Contractor details have been updated successfully.')
          setErrorAlert(false)
          scrollToTop()
          loadCompany()
          scrollToGivenIndex('.pkr')
        }
        else {
          throw new Error('Invalid response.')
        }
      })
      .catch(error => {
        console.log(error)
        setErrorAlert('Updating Contractor Failed.')
        setSuccessAlert(false)
        scrollToTop()
      })
  };

  const handleCloseModal = () => {
    setModalState(false);
  };

  const renderHeader = () => (
    <>
        <div className="nameHeader">
          <b>Company Name</b>
        </div>
        <div className="automationHeader">
          <b>Automation</b>
        </div>
    </>
  )

  // Body Section Component
  const renderBody = () => (
    <FormGroup name="workordernumber" label="">
      {companyRows.map((row) => (
        <div key={row.companyId} className='pkr'>
          <div className='pkr-input'>
            <TextInputField
              name={`company-name-${row.companyId}`}
              placeholder="Enter company name"
              value={row.companyName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                handleCompanyNameChange(row.companyId, e.target.value)
              }
            />
          </div>
          <div className='pkr-switch'>
            <Switch
              name={`automation-${row.companyId}`}
              label='Automation'
              hideLabel
              inline
              checked={row.isEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleAutomationChange(row.companyId, e.target.checked);
              }}
            />
          </div>
        </div>
      ))}
      <div className="scroll-last-index"></div>
    </FormGroup>
  )

  // Footer Section Component
  const renderFooter = () => (
    <>
      <div className="contractor-management-footer">
        <a 
          href="#" 
          onClick={(e) => { e.preventDefault(); handleAddCompany(); }} 
          className="add-company-link"
        >
          Add Company
        </a>
        <div className="footer-buttons">
          <OutlinedButton onClick={() => setModalState('discard')} size='60px' className='formDiscardButton'>
            Discard
          </OutlinedButton>
          <FilledButton onClick={handleSaveForm} size='60px' className='formSaveButton'>
            Save
          </FilledButton>
        </div>
      </div>
    </>
  )

  return (
    <>
      <ConfirmationModal
        showModal={modalState}
        onClose={handleCloseModal}
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
      />
      <FormAlerts
        showError={errorAlert}
        showSuccess={successAlert}
        onClose={clearAlerts}
      />
      <div className="contractor-card-container">
        <Card className="contractor-card-container1">
          <div className="grigItem_SearchInput companyHeader">
            {renderHeader()}
          </div>
          <div className="contractor-body ">
            {renderBody()}
          </div>
        </Card>
        <div className="contractor-footer-fixed">
          {renderFooter()}
        </div>
      </div>
    </>
  )
}
export default ContractorManagement